import { useEffect, useMemo, useRef, useState } from "react";
import type { TechnicalErrorDto } from "@/api/types";
interface LogBoxProps {
  logs: TechnicalErrorDto[];
}
const DISPLAY_CAP = 300;
const LEVEL_CLASS: Record<TechnicalErrorDto["level"], string> = {
  info: "text-ink-dim",
  warn: "text-gold",
  error: "text-sell",
};
const LEVEL_TAG: Record<TechnicalErrorDto["level"], string> = {
  info: "INFO",
  warn: "WARN",
  error: "ERR ",
};
function formatTime(ms: number): string {
  const date = new Date(ms);
  return date.toLocaleTimeString("en-GB", { hour12: false });
}
function formatLine(log: TechnicalErrorDto): string {
  const context = log.context ? ` ${JSON.stringify(log.context)}` : "";
  return `[${formatTime(log.occurredAt)}] ${LEVEL_TAG[log.level]} ${log.source} — ${log.message}${context}`;
}
const MAX_INLINE_CONTEXT_CHARS = 140;
function formatContextPreview(context: Record<string, unknown> | null): string | null {
  if (!context) return null;
  let json: string;
  try {
    json = JSON.stringify(context);
  } catch {
    return null;
  }
  if (!json || json === "{}") return null;
  return json.length > MAX_INLINE_CONTEXT_CHARS ? `${json.slice(0, MAX_INLINE_CONTEXT_CHARS)}…` : json;
}
export default function LogBox({ logs }: LogBoxProps) {
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState<"all" | "warn" | "error">("all");
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const filteredLogs = useMemo(
    () =>
      filter === "all"
        ? logs
        : logs.filter((l) => (filter === "warn" ? l.level === "warn" || l.level === "error" : l.level === "error")),
    [logs, filter],
  );
  const visibleLogs = useMemo(
    () => (autoScroll ? filteredLogs.slice(-DISPLAY_CAP) : filteredLogs),
    [filteredLogs, autoScroll],
  );
  useEffect(() => {
    if (!autoScroll || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [visibleLogs, autoScroll]);
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setAutoScroll(distanceFromBottom < 40);
  };
  const handleCopyAll = async () => {
    const text = visibleLogs.map(formatLine).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <div className="rounded-xl border border-line bg-panel shadow-panel">
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <div className="flex items-center gap-2">
          <h3 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">Live Log</h3>
          <span className="font-mono text-[10px] text-ink-faint">({visibleLogs.length})</span>
        </div>
        <div className="flex items-center gap-1.5">
          {(["all", "warn", "error"] as const).map((level) => (
            <button
              key={level}
              onClick={() => setFilter(level)}
              className={`rounded-md border px-2 py-1 font-mono text-[10px] uppercase tracking-wide transition ${
                filter === level
                  ? "border-turquoise/60 text-turquoise"
                  : "border-line text-ink-faint hover:text-ink-dim"
              }`}
            >
              {level}
            </button>
          ))}
          <button
            onClick={handleCopyAll}
            className="rounded-md border border-line px-2 py-1 font-mono text-[10px] uppercase tracking-wide text-ink-faint transition hover:border-turquoise/50 hover:text-turquoise"
          >
            {copied ? "Copied" : "Copy all"}
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-64 overflow-y-auto bg-void/60 px-4 py-2 font-mono text-[11px] leading-relaxed"
      >
        {visibleLogs.length === 0 ? (
          <div className="py-6 text-center text-ink-faint">No log lines yet. Waiting for the next cycle…</div>
        ) : (
          visibleLogs.map((log) => {
            const contextPreview = formatContextPreview(log.context);
            return (
              <div key={log.id} className="border-b border-line-soft/30 py-1 last:border-0">
                <div className={`whitespace-pre-wrap break-all ${LEVEL_CLASS[log.level]}`}>
                  <span className="text-ink-faint">[{formatTime(log.occurredAt)}]</span>{" "}
                  <span className="font-semibold">{LEVEL_TAG[log.level]}</span>{" "}
                  <span className="text-ink-dim">{log.source}</span> — {log.message}
                </div>
                {contextPreview && (
                  <div className="mt-0.5 whitespace-pre-wrap break-all pl-2 text-[10px] text-ink-faint">
                    {contextPreview}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {!autoScroll && (
        <div className="border-t border-line px-4 py-1.5 text-center">
          <button
            onClick={() => {
              setAutoScroll(true);
              if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }}
            className="font-mono text-[10px] uppercase tracking-wide text-turquoise"
          >
            ↓ Jump to latest
          </button>
        </div>
      )}
    </div>
  );
}
